import React, { useState, useEffect, useMemo } from "react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-hot-toast";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import RoleAccessTable from "@/components/backoffice/all-staff/role-access/role-access-table";
import {
  RoleListDialog,
  AddNewRoleDialog,
  EditRoleDialog,
} from "@/components/dialog/staff-manage/role-access-dialog";
import useAuth from "@/hooks/useAuth";
import hasPermission from "@/hooks/hasPermission";
import { useDispatch, useSelector } from "react-redux";
import { fetchModules } from "@/redux/slice/module-slice";
import { fetchAllFeatures } from "@/redux/slice/all-features-slice";
import { fetchAllPermissions } from "@/redux/slice/all-permissions-slice";
import { fetchAllRoles, editRole } from "@/redux/slice/all-role-slice";
import PermissionSkeleton from "@/components/skeleton/permission-skeleton";
import { restaurant_id as fallbackRestaurantId } from "@/api/lib/url";

function RoleAccess() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  // Local State
  const [moduleId, setModuleId] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [updatePermissionData, setUpdatePermissionData] = useState({});
  const [addRoleModal, setAddRoleModal] = useState(false);
  const [roleListModal, setRoleListModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [editRoleLoading, setEditRoleLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  //  Redux State
  const {
    modules,
    loading: modulesLoading,
    error: modulesError,
  } = useSelector((state) => state.modules);
  const {
    features,
    loading: featuresLoading,
    error: featuresError,
  } = useSelector((state) => state.allFeatures);
  const {
    permissions,
    loading: permissionsLoading,
    error: permissionsError,
  } = useSelector((state) => state.allPermissions);
  const { roles, error: rolesError } = useSelector((state) => state.allRoles);
  const isLoading = modulesLoading || featuresLoading || permissionsLoading;
  const hasError =
    modulesError || featuresError || permissionsError || rolesError;

  useEffect(() => {
    dispatch(fetchModules());
    dispatch(fetchAllFeatures());
    dispatch(fetchAllPermissions());
    dispatch(fetchAllRoles());
  }, [dispatch]);

  // Filter modules that have at least one feature with permissions
  const validModules = useMemo(() => {
    if (!modules || modules.length === 0) return [];
    if (!features || features.length === 0) return [];
    if (!permissions || permissions.length === 0) return [];

    return modules.filter((mod) => {
      // Find features under this module
      const moduleFeatures = features.filter((f) => f?.module_id === mod?.id);
      if (moduleFeatures.length === 0) return false;

      // Check if any of these features have at least one permission
      return moduleFeatures.some((f) =>
        permissions.some((p) => p?.feature_id === f?.id)
      );
    });
  }, [modules, features, permissions]);

  // Set default moduleId from validModules
  useEffect(() => {
    if (validModules?.length > 0) {
      if (!moduleId || !validModules.some((m) => m.id === moduleId)) {
        setModuleId(validModules[0]?.id);
      }
    }
  }, [validModules, moduleId]);

  // setUpdated permission data
  useEffect(() => {
    if (roles.length > 0 && selectedRole === "") {
      setSelectedRole(roles[0].id);
    }
    if (selectedRole && roles.length > 0) {
      const matchedRole = roles.find(
        (role) => role.id === parseInt(selectedRole)
      );
      if (matchedRole) {
        setUpdatePermissionData(matchedRole);
      }
    }
    // Reset dirty whenever the user switches role
    setIsDirty(false);
  }, [selectedRole, roles]);

  useEffect(() => {
    if (updatePermissionData?.permissions?.length > 0) {
      const updatedCheckedItems = {};
      updatePermissionData?.permissions?.forEach((permission) => {
        updatedCheckedItems[permission?.id] = true;
      });
      setCheckedItems(updatedCheckedItems);
    } else {
      setCheckedItems({});
    }
  }, [updatePermissionData]);

  const filteredFeatures = useMemo(() => {
    if (!moduleId || !features) return [];
    return features.filter((feature) => {
      if (feature?.module_id !== moduleId) return false;
      return permissions?.some((p) => p?.feature_id === feature?.id);
    });
  }, [features, permissions, moduleId]);

  const saveData = async () => {
    setEditRoleLoading(true);
    const restaurantId =
      user?.restaurant_id ||
      localStorage.getItem("restaurant_id") ||
      fallbackRestaurantId ||
      1;
    const formattedData = {
      client_id: user?.client_id || 1,
      restaurant_id: restaurantId,
      name: updatePermissionData?.name,
      description: updatePermissionData?.description,
      role_type: updatePermissionData?.role_type,
      permissions: updatePermissionData?.permissions?.map((p) => p.id),
    };
    try {
      const res = await dispatch(
        editRole({
          id: updatePermissionData?.id,
          data: formattedData,
        })
      );

      if (editRole.fulfilled.match(res)) {
        toast.success("Role updated successfully!");
        setIsDirty(false);
      } else if (editRole.rejected.match(res)) {
        toast.error(res.payload || "Failed to update role!");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong!");
    } finally {
      setEditRoleLoading(false);
    }
  };

  if (isLoading) return <PermissionSkeleton links={backofficeLinks} />;

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Role Access" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        {
          name: "Staff Management",
          path: "/backoffice/staff-management/staff/all",
        },
        {
          name: "Role Access",
          path: "/backoffice/staff-management/role-access",
        },
      ]}
    >
      <div className="w-full">
        <div className="w-full flex flex-col gap-4 mt-5 px-4 md:px-6">
          <div className="flex justify-between items-start md:items-center md:flex-row flex-col gap-5  w-full">
            <div className="flex gap-5 text-white">

            </div>

            <div className="flex items-center gap-2">
              {hasPermission("back_office.staff_management.create_role") && (
                <Button
                  onClick={() => setAddRoleModal(true)}
                  className="bg-cyellow hover:bg-cyellow text-black"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add New Role
                </Button>
              )}
              {hasPermission("back_office.staff_management.role_list") && (
                <Button onClick={() => setRoleListModal(true)} variant="outline">
                  Role list
                </Button>
              )}
              <AddNewRoleDialog
                addRoleModal={addRoleModal}
                setAddRoleModal={setAddRoleModal}
              />
              <RoleListDialog
                loading={isLoading}
                roles={roles}
                roleListDialog={roleListModal}
                setRoleListModal={setRoleListModal}
              />
            </div>
          </div>
          <div className="bg-[#161717] rounded-md px-5 w-full">
            <div className="flex p-5 justify-between items-center flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <h3 className="text-cwhite font-medium">Role</h3>
                <Select
                  value={selectedRole}
                  onValueChange={(value) => setSelectedRole(value)}
                >
                  <SelectTrigger className="w-[140px]">
                    {/* {updatePermissionData?.name || "Not Available"} */}
                    <SelectValue placeholder="Select Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {roles?.length > 0 ? (
                        roles.map((role) => (
                          <SelectItem key={role?.id} value={role?.id}>
                            {role?.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-1 text-muted-foreground">
                          No Role Available
                        </div>
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-5 items-center">
                {isDirty && (
                  <Button
                    isLoading={editRoleLoading}
                    onClick={saveData}
                    className="bg-cgreen text-black px-8"
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>

            <div className="w-full overflow-hidden">
              <div className="bg-[#2D2E2F] px-4 flex gap-1.5 items-center overflow-x-auto custom-scrollbar w-full rounded-md">
                {validModules?.length === 0 ? (
                  <div className="px-4 py-2 text-xs text-neutral-400">
                    No modules with permissions found.
                  </div>
                ) : (
                  validModules?.map((module) => (
                    <div
                      key={module?.id}
                      onClick={() => setModuleId(module?.id)}
                      className={`${
                        module?.id === moduleId
                          ? "bg-cyellow rounded !text-black font-medium"
                          : ""
                      } py-2 px-4 bg-[#1F1F1F] text-cwhite flex-shrink-0 text-sm font-medium cursor-pointer transition-colors duration-150`}
                    >
                      {module?.module_name}
                    </div>
                  ))
                )}
              </div>
              <div className="mt-5 w-full overflow-hidden">
                {roles?.length > 0 ? (
                  filteredFeatures?.length > 0 ? (
                    <RoleAccessTable
                      setUpdatePermissionData={setUpdatePermissionData}
                      allFeatures={filteredFeatures}
                      allPermissions={permissions}
                      checkedItems={checkedItems}
                      setCheckedItems={setCheckedItems}
                      onDirty={() => setIsDirty(true)}
                    />
                  ) : (
                    <div className="flex items-center justify-center py-10 text-sm text-cwhite3 italic">
                      No features available for this module.
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default RoleAccess;
